import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { RemoteFixRow } from './buildRemoteFixesModel';

type RemoteFixesListProps = {
  rows: RemoteFixRow[];
  onRetry: (row: RemoteFixRow) => void;
  retryingId: string | null;
};

/** Fix log rows — Ghost Screen Remote Fixes.dc.html. */
export function RemoteFixesList({ rows, onRetry, retryingId }: RemoteFixesListProps) {
  const { t } = useTranslation('operations');
  return (
    <div
      className="flex flex-col overflow-hidden rounded-[14px] border"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      {rows.map((row, index) => (
        <div
          key={row.id}
          className="flex flex-wrap items-center gap-x-6 gap-y-3.5 px-5 py-4"
          style={{ borderTop: index ? '1px solid var(--gs-line)' : undefined }}
        >
          <div className="flex min-w-0 flex-[2_1_240px] flex-col gap-1">
            <span className="text-[15px] font-bold" style={{ color: 'var(--gs-ink)' }}>
              {row.label}
            </span>
            <span
              className="flex flex-wrap items-center gap-1.5 text-[13.5px]"
              style={{ color: 'var(--gs-muted)' }}
            >
              <Trans
                t={t}
                i18nKey="row.onScreen"
                values={{ name: row.screenName }}
                components={{
                  screen: row.screenId ? (
                    <Link
                      to={`/screens/${encodeURIComponent(row.screenId)}`}
                      className="bg-transparent p-0 text-[13.5px] font-bold no-underline"
                      style={{ color: 'var(--gs-accent)', border: 'none', fontFamily: 'inherit' }}
                    />
                  ) : (
                    <span className="font-bold" style={{ color: 'var(--gs-ink2)' }} />
                  ),
                }}
              />
              {row.locationName ? <span>· {row.locationName}</span> : null}
            </span>
          </div>

          <div className="flex min-w-0 flex-[2_1_220px] flex-col gap-[5px]">
            <span
              className="inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12.5px] font-bold"
              style={{ color: row.statusColor, borderColor: 'var(--gs-line)' }}
            >
              <span
                className="h-[7px] w-[7px] rounded-full"
                style={{ background: row.statusColor }}
                aria-hidden
              />
              {row.statusLabel}
            </span>
            {row.note ? (
              <span className="text-[13px]" style={{ color: 'var(--gs-ink2)', lineHeight: 1.4 }}>
                {row.note}
              </span>
            ) : null}
          </div>

          <div
            className="flex flex-[1_1_140px] flex-col gap-[3px] text-[13px]"
            style={{ color: 'var(--gs-muted)' }}
          >
            <span className="font-semibold" style={{ color: 'var(--gs-ink2)' }}>
              {row.issuedBy}
            </span>
            <span>{row.when}</span>
          </div>

          <div className="flex min-w-[110px] flex-none justify-end">
            {row.canRetry ? (
              <button
                type="button"
                disabled={retryingId === row.id}
                onClick={() => onRetry(row)}
                className="rounded-[9px] border px-[13px] py-2 text-[13.5px] font-bold disabled:opacity-50"
                style={{
                  color: 'var(--gs-ink)',
                  background: 'var(--gs-panel)',
                  borderColor: 'var(--gs-line)',
                  fontFamily: 'inherit',
                }}
              >
                {retryingId === row.id ? t('row.retrying') : t('row.retry')}
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
